import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, Plus, Wallet, KeyRound, Eye, EyeOff, RefreshCw, Trash, Camera, Upload, Download, X } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
    "Cameriere",
    "Cameriere senior",
    "Maître",
    "Sommelier",
    "Barman",
    "Chef",
    "Sous-chef",
    "Pizzaiolo",
    "Pasticciere",
    "Lavapiatti",
    "Hostess",
    "Runner",
];

const ZONES = ["Sala", "Cucina", "Magazzino", "Bancone bar", "Pizzeria"];

const EMOJI_OPTIONS = [
    "👤", "👨‍🍳", "👩‍🍳", "🧑‍🍳", "👨‍💼", "👩‍💼",
    "🍕", "🍝", "🥂", "🍷", "🍺", "🍹",
    "🧑", "👨", "👩", "🧔", "👨‍🦰", "👩‍🦰",
    "🤵", "🙋‍♂️", "🙋‍♀️", "💂", "🦸", "🧙",
    "⭐", "🔥", "💎", "🎯", "🚀", "💪",
];

// Resize image to ~200x200 max while keeping aspect ratio. Returns base64 JPEG data URI.
async function fileToResizedDataUrl(file, maxSize = 240, quality = 0.82) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                let { width, height } = img;
                if (width > height && width > maxSize) {
                    height = Math.round((height * maxSize) / width);
                    width = maxSize;
                } else if (height > maxSize) {
                    width = Math.round((width * maxSize) / height);
                    height = maxSize;
                }
                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, width, height);
                resolve(canvas.toDataURL("image/jpeg", quality));
            };
            img.onerror = reject;
            img.src = e.target.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

const empty = {
    name: "",
    email: "",
    role: "Cameriere",
    phone: "",
    employment_type: "on_call",
    hourly_rate: 10,
    weekly_hours: 40,
    monthly_net: 1300,
    extra_hourly_rate: 0,
    skip_hourly_cost: false,
    default_zone: "Sala",
    emoji: "👤",
    photo_url: null,
    status: "active",
    notes: "",
};

export default function EmployeesPage() {
    const [items, setItems] = useState([]);
    const [limit, setLimit] = useState(0);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [edit, setEdit] = useState(null);
    const [form, setForm] = useState(empty);
    const [payOpen, setPayOpen] = useState(false);
    const [payEmp, setPayEmp] = useState(null);
    const [payments, setPayments] = useState([]);
    const [showPwd, setShowPwd] = useState(false);
    const [newPay, setNewPay] = useState({
        amount: "",
        date: new Date().toISOString().slice(0, 10),
        category: "stipendio",
        notes: "",
    });

    const openPayments = async (e) => {
        setPayEmp(e);
        setShowPwd(false);
        setNewPay({
            amount: "",
            date: new Date().toISOString().slice(0, 10),
            category: "stipendio",
            notes: "",
        });
        try {
            const { data } = await api.get("/payments", {
                params: { employee_id: e.id },
            });
            setPayments(data.items || []);
        } catch (err) {
            setPayments([]);
        }
        setPayOpen(true);
    };

    const addPayment = async () => {
        if (!payEmp) return;
        if (!newPay.amount || parseFloat(newPay.amount) <= 0) {
            toast.error("Inserisci un importo valido");
            return;
        }
        try {
            await api.post("/payments", {
                employee_id: payEmp.id,
                amount: parseFloat(newPay.amount),
                date: newPay.date,
                category: newPay.category,
                notes: newPay.notes,
            });
            toast.success("Pagamento registrato");
            const { data } = await api.get("/payments", {
                params: { employee_id: payEmp.id },
            });
            setPayments(data.items || []);
            setNewPay({
                ...newPay,
                amount: "",
                notes: "",
            });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore");
        }
    };

    const removePayment = async (id) => {
        if (!window.confirm("Eliminare questo pagamento?")) return;
        try {
            await api.delete(`/payments/${id}`);
            setPayments(payments.filter((p) => p.id !== id));
            toast.success("Pagamento eliminato");
        } catch (e) {
            toast.error("Errore");
        }
    };

    const exportEmployeesCSV = () => {
        try {
            if (items.length === 0) {
                toast.error("Nessun dipendente da esportare");
                return;
            }
            const header = [
                "name",
                "email",
                "role",
                "phone",
                "employment_type",
                "hourly_rate",
                "weekly_hours",
                "monthly_net",
                "extra_hourly_rate",
                "skip_hourly_cost",
                "default_zone",
                "emoji",
                "status",
                "notes",
            ];
            const esc = (v) => {
                if (v == null) return "";
                const s = String(v).replace(/"/g, '""');
                return /[",\n;]/.test(s) ? `"${s}"` : s;
            };
            const rows = items.map((e) =>
                header.map((k) => esc(e[k] ?? "")).join(","),
            );
            const csv = [header.join(","), ...rows].join("\n");
            const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `dipendenti_${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success(`${items.length} dipendenti esportati`);
        } catch (e) {
            toast.error("Errore esportazione");
        }
    };

    const importEmployeesCSV = async (file) => {
        try {
            const text = await file.text();
            const clean = text.replace(/^\uFEFF/, "");
            const lines = clean.split(/\r?\n/).filter((l) => l.trim());
            if (lines.length < 2) {
                toast.error("CSV vuoto");
                return;
            }
            const parseLine = (line) => {
                const out = [];
                let cur = "";
                let inQ = false;
                for (let i = 0; i < line.length; i++) {
                    const c = line[i];
                    if (inQ) {
                        if (c === '"' && line[i + 1] === '"') {
                            cur += '"';
                            i++;
                        } else if (c === '"') inQ = false;
                        else cur += c;
                    } else {
                        if (c === '"') inQ = true;
                        else if (c === ",") {
                            out.push(cur);
                            cur = "";
                        } else cur += c;
                    }
                }
                out.push(cur);
                return out;
            };
            const headers = parseLine(lines[0]).map((h) => h.trim().toLowerCase());
            const rows = lines.slice(1).map((line) => {
                const cells = parseLine(line);
                const obj = {};
                headers.forEach((h, i) => {
                    obj[h] = (cells[i] || "").trim();
                });
                return obj;
            });
            const valid = rows.filter((r) => r.name);
            if (valid.length === 0) {
                toast.error("Nessuna riga valida (manca 'name')");
                return;
            }
            const overwrite = window.confirm(
                `Importare ${valid.length} dipendenti?\n\n` +
                    `OK = SOSTITUISCI tutti i dipendenti attuali (backup automatico).\n` +
                    `Annulla = AGGIUNGI ai dipendenti esistenti.`,
            );
            const { data } = await api.post("/employees/import", {
                employees: valid,
                overwrite,
            });
            toast.success(`${data.count} dipendenti importati`);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Errore importazione");
        }
    };

    const regenerateFromForm = async () => {
        if (!edit?.id) {
            toast.error("Salva prima il dipendente per generare le credenziali");
            return;
        }
        const isNew = !edit.portal_username;
        const msg = isNew
            ? "Generare le credenziali per il portale dipendente?"
            : "Rigenerare le credenziali? La password attuale sarà sostituita.";
        if (!window.confirm(msg)) return;
        try {
            const { data } = await api.post(`/employees/${edit.id}/reset-portal-password`);
            setCredModal({
                name: edit.name,
                username: data.username,
                password: data.password,
            });
            toast.success(isNew ? "Credenziali generate" : "Credenziali rigenerate");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Errore");
        }
    };

    const regeneratePassword = async () => {
        if (!payEmp) return;
        if (!window.confirm("Rigenerare la password? Quella attuale verrà sostituita.")) return;
        try {
            const { data } = await api.post(`/employees/${payEmp.id}/reset-portal-password`);
            setPayEmp({ ...payEmp, portal_username: data.username, portal_password: data.password });
            setShowPwd(true);
            toast.success("Password rigenerata");
            load();
        } catch (e) {
            toast.error("Errore rigenerazione");
        }
    };

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/employees");
            setItems(data.items);
            setLimit(data.limit);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const startCreate = () => {
        setEdit(null);
        setForm(empty);
        setOpen(true);
    };
    const startEdit = (e) => {
        setEdit(e);
        setForm({ ...empty, ...e });
        setOpen(true);
    };

    const [credModal, setCredModal] = useState(null); // {name, username, password}

    const save = async () => {
        try {
            if (edit) {
                await api.put(`/employees/${edit.id}`, form);
                toast.success("Dipendente aggiornato");
                setOpen(false);
                load();
            } else {
                const { data } = await api.post("/employees", form);
                toast.success("Dipendente aggiunto");
                setOpen(false);
                load();
                if (data.portal_username && data.portal_password) {
                    setCredModal({
                        name: data.name,
                        username: data.portal_username,
                        password: data.portal_password,
                    });
                }
            }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const remove = async (e) => {
        if (!confirm(`Eliminare ${e.name}?`)) return;
        try {
            await api.delete(`/employees/${e.id}`);
            toast.success("Eliminato");
            load();
        } catch (e2) {
            toast.error(formatApiError(e2.response?.data?.detail));
        }
    };

    const activeCount = items.filter((i) => i.status === "active").length;
    const atLimit = activeCount >= limit;

    return (
        <div data-testid="employees-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Anagrafica</div>
                    <h1 className="font-display text-4xl text-navy">
                        Dipendenti & brigata
                    </h1>
                    <p className="text-sf-secondary mt-1">
                        {activeCount} attivi su {limit === 9999 ? "∞" : limit}{" "}
                        consentiti dal piano.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        onClick={exportEmployeesCSV}
                        data-testid="employees-csv-export"
                        className="border-sf-secondary text-sf-secondary"
                        disabled={items.length === 0}
                    >
                        <Download size={14} className="mr-2" />
                        Salva CSV
                    </Button>
                    <label className="inline-flex items-center gap-2 cursor-pointer border border-sf-secondary text-sf-secondary px-3 py-2 rounded-md text-sm font-medium hover:bg-[#fafaf7]">
                        <Upload size={14} />
                        Importa CSV
                        <input
                            type="file"
                            accept=".csv,text/csv"
                            className="hidden"
                            data-testid="employees-csv-import"
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) importEmployeesCSV(f);
                                e.target.value = "";
                            }}
                        />
                    </label>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button
                            className="bg-navy text-white hover:bg-navy/90 lift"
                            onClick={startCreate}
                            disabled={atLimit}
                            data-testid="employees-add-btn"
                        >
                            <Plus size={16} className="mr-2" /> Aggiungi
                        </Button>
                    </DialogTrigger>
                    <DialogContent
                        className="max-w-lg max-h-[90vh] overflow-y-auto"
                        data-testid="employee-dialog"
                    >
                        <DialogHeader>
                            <DialogTitle className="font-display text-2xl">
                                {edit ? "Modifica" : "Nuovo"} dipendente
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3">
                            <Field label="Nome">
                                <Input
                                    value={form.name}
                                    onChange={(e) =>
                                        setForm({ ...form, name: e.target.value })
                                    }
                                    data-testid="employee-form-name"
                                />
                            </Field>

                            <Field label="Tipologia contratto">
                                <div className="inline-flex w-full bg-[#fafaf7] border border-sf rounded-md p-1">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setForm({ ...form, employment_type: "on_call" })
                                        }
                                        className={`flex-1 px-3 py-1.5 text-sm rounded font-semibold transition-colors ${form.employment_type === "on_call" ? "bg-navy text-white" : "text-sf-secondary"}`}
                                        data-testid="employee-form-type-oncall"
                                    >
                                        A chiamata
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setForm({ ...form, employment_type: "employee" })
                                        }
                                        className={`flex-1 px-3 py-1.5 text-sm rounded font-semibold transition-colors ${form.employment_type === "employee" ? "bg-navy text-white" : "text-sf-secondary"}`}
                                        data-testid="employee-form-type-employee"
                                    >
                                        Dipendente
                                    </button>
                                </div>
                            </Field>

                            <Field label="Ruolo">
                                <Select
                                    value={form.role}
                                    onValueChange={(v) =>
                                        setForm({ ...form, role: v })
                                    }
                                >
                                    <SelectTrigger data-testid="employee-form-role">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ROLES.map((r) => (
                                            <SelectItem key={r} value={r}>
                                                {r}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>

                            <Field label="Zona di lavoro principale">
                                <Select
                                    value={form.default_zone || "Sala"}
                                    onValueChange={(v) =>
                                        setForm({ ...form, default_zone: v })
                                    }
                                >
                                    <SelectTrigger data-testid="employee-form-zone">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ZONES.map((z) => (
                                            <SelectItem key={z} value={z}>
                                                {z}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <div className="text-xs text-sf-muted mt-1">
                                    Usata come postazione di default quando crei i turni.
                                </div>
                            </Field>

                            <Field label="Foto del dipendente">
                                <div className="flex items-center gap-3">
                                    <div className="w-20 h-20 rounded-full bg-[#fafaf7] border-2 border-sf overflow-hidden flex items-center justify-center">
                                        {form.photo_url ? (
                                            <img
                                                src={form.photo_url}
                                                alt="avatar"
                                                className="w-full h-full object-cover"
                                                data-testid="emp-photo-preview"
                                            />
                                        ) : (
                                            <span className="text-4xl">{form.emoji || "👤"}</span>
                                        )}
                                    </div>
                                    <div className="flex-1 space-y-1.5">
                                        <div className="flex flex-wrap gap-1.5">
                                            <label className="inline-flex items-center gap-1.5 cursor-pointer border border-navy text-navy px-3 py-1.5 rounded-md text-xs font-medium hover:bg-navy/5">
                                                <Upload size={12} />
                                                Carica foto
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    className="hidden"
                                                    data-testid="emp-photo-upload"
                                                    onChange={async (e) => {
                                                        const f = e.target.files?.[0];
                                                        if (!f) return;
                                                        try {
                                                            const url = await fileToResizedDataUrl(f);
                                                            setForm({ ...form, photo_url: url });
                                                            toast.success("Foto caricata");
                                                        } catch (err) {
                                                            toast.error("Errore caricamento foto");
                                                        }
                                                        e.target.value = "";
                                                    }}
                                                />
                                            </label>
                                            <label className="inline-flex items-center gap-1.5 cursor-pointer border border-amber-brand text-amber-brand px-3 py-1.5 rounded-md text-xs font-medium hover:bg-amber-brand/10">
                                                <Camera size={12} />
                                                Scatta foto
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    capture="environment"
                                                    className="hidden"
                                                    data-testid="emp-photo-capture"
                                                    onChange={async (e) => {
                                                        const f = e.target.files?.[0];
                                                        if (!f) return;
                                                        try {
                                                            const url = await fileToResizedDataUrl(f);
                                                            setForm({ ...form, photo_url: url });
                                                            toast.success("Foto scattata");
                                                        } catch (err) {
                                                            toast.error("Errore");
                                                        }
                                                        e.target.value = "";
                                                    }}
                                                />
                                            </label>
                                            {form.photo_url && (
                                                <button
                                                    type="button"
                                                    onClick={() => setForm({ ...form, photo_url: null })}
                                                    className="inline-flex items-center gap-1.5 border border-red-500 text-red-600 px-3 py-1.5 rounded-md text-xs font-medium hover:bg-red-50"
                                                    data-testid="emp-photo-remove"
                                                >
                                                    <X size={12} />
                                                    Rimuovi
                                                </button>
                                            )}
                                        </div>
                                        <div className="text-xs text-sf-muted">
                                            Se presente, la foto sostituisce l&apos;emoji nel calendario. Max 240×240 px (riduzione automatica).
                                        </div>
                                    </div>
                                </div>
                            </Field>

                            <Field label="Emoji (avatar nel calendario)">
                                <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto p-2 bg-[#fafaf7] border border-sf rounded-md">
                                    {EMOJI_OPTIONS.map((e) => (
                                        <button
                                            key={e}
                                            type="button"
                                            onClick={() =>
                                                setForm({ ...form, emoji: e })
                                            }
                                            className={`text-2xl w-10 h-10 rounded transition ${form.emoji === e ? "bg-amber-brand/30 ring-2 ring-amber-brand" : "hover:bg-white"}`}
                                            data-testid={`emp-emoji-${e}`}
                                        >
                                            {e}
                                        </button>
                                    ))}
                                </div>
                                <div className="text-xs text-sf-muted mt-1">
                                    Visibile come avatar nel calendario turni.
                                </div>
                            </Field>

                            {form.employment_type === "on_call" ? (
                                <Field label="Tariffa oraria (€)">
                                    <Input
                                        type="number"
                                        step="0.5"
                                        min="0"
                                        value={form.hourly_rate}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                hourly_rate: parseFloat(
                                                    e.target.value || 0
                                                ),
                                            })
                                        }
                                        data-testid="employee-form-rate"
                                    />
                                </Field>
                            ) : (
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Ore pattuite / sett.">
                                        <Input
                                            type="number"
                                            step="0.5"
                                            min="0"
                                            value={form.weekly_hours}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    weekly_hours: parseFloat(
                                                        e.target.value || 0
                                                    ),
                                                })
                                            }
                                            data-testid="employee-form-weekly-hours"
                                        />
                                    </Field>
                                    <Field label="Netto mensile (€)">
                                        <Input
                                            type="number"
                                            step="10"
                                            min="0"
                                            value={form.monthly_net}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    monthly_net: parseFloat(
                                                        e.target.value || 0
                                                    ),
                                                })
                                            }
                                            data-testid="employee-form-monthly-net"
                                        />
                                    </Field>
                                </div>
                            )}

                            <Field label="Extra busta - importo mensile (€)">
                                <Input
                                    type="number"
                                    step="10"
                                    min="0"
                                    value={form.extra_hourly_rate}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            extra_hourly_rate: parseFloat(
                                                e.target.value || 0
                                            ),
                                        })
                                    }
                                    placeholder="0 = nessun extra"
                                    data-testid="employee-form-extra-rate"
                                />
                                <div className="text-xs text-sf-muted mt-1">
                                    Importo mensile fuori busta paga (mance fisse,
                                    bonus, integrazioni). Distribuito nelle
                                    statistiche giornaliere/settimanali.
                                </div>
                            </Field>

                            <div className="flex items-start gap-2 bg-[#fafaf7] border border-sf rounded-md p-3">
                                <input
                                    type="checkbox"
                                    id="skip-hourly-cost"
                                    checked={!!form.skip_hourly_cost}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            skip_hourly_cost: e.target.checked,
                                        })
                                    }
                                    data-testid="employee-form-skip-hourly"
                                    className="w-4 h-4 accent-navy mt-0.5"
                                />
                                <label
                                    htmlFor="skip-hourly-cost"
                                    className="cursor-pointer text-sm"
                                >
                                    <div className="font-medium text-navy">
                                        Non calcolare ore extra
                                    </div>
                                    <div className="text-xs text-sf-muted mt-0.5">
                                        Le ore lavorate da questo dipendente NON
                                        generano costo orario nelle statistiche
                                        (utile per titolari, familiari o personale
                                        con compenso fisso).
                                    </div>
                                </label>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Email">
                                    <Input
                                        type="email"
                                        value={form.email || ""}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                email: e.target.value,
                                            })
                                        }
                                        data-testid="employee-form-email"
                                    />
                                </Field>
                                <Field label="Telefono">
                                    <Input
                                        value={form.phone || ""}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                phone: e.target.value,
                                            })
                                        }
                                        data-testid="employee-form-phone"
                                    />
                                </Field>
                            </div>
                            <Field label="Stato">
                                <Select
                                    value={form.status}
                                    onValueChange={(v) =>
                                        setForm({ ...form, status: v })
                                    }
                                >
                                    <SelectTrigger data-testid="employee-form-status">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="active">
                                            Attivo
                                        </SelectItem>
                                        <SelectItem value="inactive">
                                            Inattivo
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </Field>

                            {/* Credenziali portale */}
                            <Field label="Credenziali portale dipendente">
                                <div className="bg-amber-brand/10 border border-amber-brand/30 rounded-md p-3 space-y-2">
                                    {edit?.portal_username ? (
                                        <div className="text-sm space-y-1">
                                            <div>
                                                <span className="text-xs text-sf-muted">Username:</span>{" "}
                                                <span className="font-mono font-semibold select-all" data-testid="emp-form-username">
                                                    {edit.portal_username}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="text-xs text-sf-muted">Password:</span>{" "}
                                                <span className="font-mono font-semibold select-all" data-testid="emp-form-password">
                                                    {edit.portal_password || "••••••••"}
                                                </span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-xs text-sf-secondary italic">
                                            {edit?.id
                                                ? "Credenziali non ancora generate per questo dipendente."
                                                : "Salva il dipendente per generare automaticamente le credenziali."}
                                        </div>
                                    )}
                                    {edit?.id && (
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            onClick={regenerateFromForm}
                                            className="border-navy text-navy"
                                            data-testid="emp-form-generate-credentials"
                                        >
                                            <KeyRound size={12} className="mr-1.5" />
                                            {edit.portal_username
                                                ? "Rigenera credenziali"
                                                : "Genera credenziali"}
                                        </Button>
                                    )}
                                </div>
                            </Field>
                        </div>
                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => setOpen(false)}
                                data-testid="employee-form-cancel"
                            >
                                Annulla
                            </Button>
                            <Button
                                onClick={save}
                                className="bg-navy text-white"
                                data-testid="employee-form-save"
                            >
                                Salva
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                </div>

                <Dialog open={!!credModal} onOpenChange={(o) => !o && setCredModal(null)}>
                    <DialogContent data-testid="cred-modal" className="max-w-md max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-display text-2xl flex items-center gap-2">
                                <KeyRound size={20} className="text-amber-brand" />
                                Credenziali generate
                            </DialogTitle>
                        </DialogHeader>
                        {credModal && (
                            <div className="space-y-3">
                                <p className="text-sm text-sf-secondary">
                                    Credenziali per il portale di <strong>{credModal.name}</strong>. Conservale o condividile col dipendente — la password sarà comunque visibile nella scheda &quot;Pagamenti & Credenziali&quot;.
                                </p>
                                <div className="bg-amber-brand/10 border border-amber-brand/40 rounded-md p-3 font-mono text-sm space-y-2">
                                    <div>
                                        <span className="text-sf-muted text-xs">Username:</span>
                                        <div className="font-bold text-navy select-all" data-testid="cred-username">
                                            {credModal.username}
                                        </div>
                                    </div>
                                    <div>
                                        <span className="text-sf-muted text-xs">Password:</span>
                                        <div className="font-bold text-navy select-all" data-testid="cred-password">
                                            {credModal.password}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-xs text-sf-muted">
                                    Login: <a href="/portal-login" target="_blank" rel="noreferrer" className="underline">/portal-login</a>
                                </div>
                            </div>
                        )}
                        <DialogFooter>
                            <Button
                                onClick={() => {
                                    if (credModal) {
                                        navigator.clipboard.writeText(
                                            `Login: ${window.location.origin}/portal-login\nUsername: ${credModal.username}\nPassword: ${credModal.password}`,
                                        );
                                        toast.success("Copiate negli appunti");
                                    }
                                }}
                                variant="outline"
                            >
                                Copia tutto
                            </Button>
                            <Button onClick={() => setCredModal(null)} className="bg-navy text-white">
                                OK, ho preso nota
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                <Dialog open={payOpen} onOpenChange={setPayOpen}>
                    <DialogContent data-testid="payments-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-display text-2xl flex items-center gap-2">
                                <Wallet size={20} />
                                Pagamenti & Credenziali — {payEmp?.name}
                            </DialogTitle>
                        </DialogHeader>
                        {payEmp && (
                            <div className="space-y-5">
                                {/* Credenziali portale */}
                                <div className="bg-amber-brand/10 border border-amber-brand/30 rounded-md p-3">
                                    <div className="flex items-center gap-2 label-eyebrow text-navy mb-2">
                                        <KeyRound size={14} />
                                        Credenziali portale dipendente
                                    </div>
                                    {payEmp.portal_username ? (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                                            <div>
                                                <div className="text-xs text-sf-muted">Username</div>
                                                <div className="font-mono font-medium select-all" data-testid="emp-portal-username">
                                                    {payEmp.portal_username}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-sf-muted">Password</div>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-mono font-medium select-all" data-testid="emp-portal-password">
                                                        {showPwd ? payEmp.portal_password : "••••••••"}
                                                    </span>
                                                    <button
                                                        onClick={() => setShowPwd(!showPwd)}
                                                        className="text-sf-muted hover:text-navy"
                                                    >
                                                        {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="sm:col-span-2 flex gap-2 mt-1">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={regeneratePassword}
                                                    data-testid="emp-regen-pwd"
                                                >
                                                    <RefreshCw size={12} className="mr-1.5" />
                                                    Rigenera password
                                                </Button>
                                                <div className="text-xs text-sf-muted self-center">
                                                    Login: <a href="/portal-login" target="_blank" rel="noreferrer" className="underline">/portal-login</a>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-sm text-sf-secondary">
                                            Credenziali non ancora generate. Salva il dipendente con &quot;Portale abilitato&quot; per generarle.
                                        </div>
                                    )}
                                </div>

                                {/* Nuovo pagamento */}
                                <div className="border border-sf rounded-md p-3">
                                    <div className="label-eyebrow text-navy mb-2">Registra nuovo pagamento</div>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                        <div>
                                            <Label className="text-xs">Importo €</Label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={newPay.amount}
                                                onChange={(e) => setNewPay({ ...newPay, amount: e.target.value })}
                                                data-testid="pay-amount"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-xs">Data</Label>
                                            <Input
                                                type="date"
                                                value={newPay.date}
                                                onChange={(e) => setNewPay({ ...newPay, date: e.target.value })}
                                                data-testid="pay-date"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-xs">Categoria</Label>
                                            <Select
                                                value={newPay.category}
                                                onValueChange={(v) => setNewPay({ ...newPay, category: v })}
                                            >
                                                <SelectTrigger data-testid="pay-category">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="stipendio">Stipendio</SelectItem>
                                                    <SelectItem value="extra_busta">Extra busta</SelectItem>
                                                    <SelectItem value="ore_lavorate">Ore lavorate</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className="text-xs">Note</Label>
                                            <Input
                                                value={newPay.notes}
                                                onChange={(e) => setNewPay({ ...newPay, notes: e.target.value })}
                                                placeholder="bonifico, contanti..."
                                                data-testid="pay-notes"
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-3 flex justify-end">
                                        <Button
                                            onClick={addPayment}
                                            className="bg-navy text-white"
                                            data-testid="pay-add"
                                        >
                                            Registra pagamento
                                        </Button>
                                    </div>
                                </div>

                                {/* Lista pagamenti */}
                                <div>
                                    <div className="label-eyebrow text-navy mb-2">Storico pagamenti</div>
                                    <div className="border border-sf rounded-md overflow-hidden">
                                        <table className="w-full text-sm">
                                            <thead className="bg-[#fafaf7]">
                                                <tr>
                                                    <th className="px-3 py-2 text-left label-eyebrow">Data</th>
                                                    <th className="px-3 py-2 text-left label-eyebrow">Categoria</th>
                                                    <th className="px-3 py-2 text-right label-eyebrow">Importo</th>
                                                    <th className="px-3 py-2 text-left label-eyebrow">Note</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {payments.length === 0 && (
                                                    <tr>
                                                        <td colSpan={5} className="text-center py-6 text-sf-muted">
                                                            Nessun pagamento
                                                        </td>
                                                    </tr>
                                                )}
                                                {payments.map((p) => (
                                                    <tr key={p.id} className="border-t border-sf">
                                                        <td className="px-3 py-2 font-medium">{p.date}</td>
                                                        <td className="px-3 py-2">
                                                            {p.category === "stipendio" ? "Stipendio" : p.category === "extra_busta" ? "Extra busta" : "Ore lavorate"}
                                                        </td>
                                                        <td className="px-3 py-2 text-right font-mono font-bold text-green-700">
                                                            €{p.amount.toFixed(2)}
                                                        </td>
                                                        <td className="px-3 py-2 text-sf-muted text-xs">{p.notes}</td>
                                                        <td className="px-3 py-2 text-right">
                                                            <button
                                                                onClick={() => removePayment(p.id)}
                                                                className="text-red-600 hover:text-red-700"
                                                            >
                                                                <Trash size={12} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setPayOpen(false)}>
                                Chiudi
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            {atLimit && (
                <div
                    className="mb-4 p-3 bg-amber-brand/15 border border-amber-brand text-navy rounded-md text-sm"
                    data-testid="employees-limit-warning"
                >
                    Hai raggiunto il limite di {limit} dipendenti del tuo piano.{" "}
                    <a href="/pricing" className="underline font-bold">
                        Aggiorna a Pro
                    </a>{" "}
                    per dipendenti illimitati.
                </div>
            )}

            <div
                className="bg-white border border-sf rounded-xl overflow-hidden"
                data-testid="employees-table-card"
            >
                <Table data-testid="employees-table">
                    <TableHeader>
                        <TableRow>
                            <TableHead>Nome</TableHead>
                            <TableHead>Ruolo</TableHead>
                            <TableHead>Zona</TableHead>
                            <TableHead>Tipo</TableHead>
                            <TableHead>Contatti</TableHead>
                            <TableHead className="text-right">Compenso</TableHead>
                            <TableHead className="text-right">Extra €/mese</TableHead>
                            <TableHead>Stato</TableHead>
                            <TableHead className="text-right">Azioni</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={9} className="py-8 text-center text-sf-muted">
                                    Caricamento…
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && items.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={9} className="py-12 text-center text-sf-muted">
                                    Nessun dipendente. Aggiungi il primo.
                                </TableCell>
                            </TableRow>
                        )}
                        {items.map((e) => {
                            const type = e.employment_type || "on_call";
                            const isOnCall = type === "on_call";
                            return (
                            <TableRow
                                key={e.id}
                                data-testid={`employee-row-${e.id}`}
                            >
                                <TableCell className="font-medium">
                                    {e.photo_url ? (
                                        <img
                                            src={e.photo_url}
                                            alt={e.name}
                                            className="inline-block w-8 h-8 rounded-full object-cover mr-2 align-middle"
                                        />
                                    ) : (
                                        <span className="text-xl mr-2">{e.emoji || "👤"}</span>
                                    )}
                                    {e.name}
                                </TableCell>
                                <TableCell>{e.role}</TableCell>
                                <TableCell>
                                    <Badge
                                        variant="outline"
                                        className="border-sf text-sf-secondary"
                                    >
                                        {e.default_zone || "Sala"}
                                    </Badge>
                                </TableCell>
                                <TableCell>
                                    <Badge
                                        variant="outline"
                                        className={isOnCall
                                            ? "border-amber-brand text-navy bg-amber-brand/10"
                                            : "border-navy text-navy bg-navy/5"}
                                    >
                                        {isOnCall ? "A chiamata" : "Dipendente"}
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-sm text-sf-secondary">
                                    {e.email || ""}{" "}
                                    {e.phone ? `· ${e.phone}` : ""}
                                </TableCell>
                                <TableCell className="text-right font-mono text-sm">
                                    {isOnCall ? (
                                        <>€{(e.hourly_rate ?? 0).toFixed(2)}/h</>
                                    ) : (
                                        <div className="text-xs leading-tight">
                                            <div className="font-semibold text-navy">€{(e.monthly_net ?? 0).toFixed(0)}/mese</div>
                                            <div className="text-sf-muted">{(e.weekly_hours ?? 0)}h/sett.</div>
                                        </div>
                                    )}
                                </TableCell>
                                <TableCell className="text-right font-mono text-sm">
                                    {(e.extra_hourly_rate ?? 0) > 0 ? (
                                        <span className="text-amber-brand font-semibold">
                                            €{e.extra_hourly_rate.toFixed(2)}
                                        </span>
                                    ) : (
                                        <span className="text-sf-muted">—</span>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Badge
                                        variant={
                                            e.status === "active"
                                                ? "default"
                                                : "secondary"
                                        }
                                        className={
                                            e.status === "active"
                                                ? "bg-[#2d6a4f] text-white"
                                                : ""
                                        }
                                    >
                                        {e.status === "active"
                                            ? "Attivo"
                                            : "Inattivo"}
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-right">
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => openPayments(e)}
                                        data-testid={`employee-payments-${e.id}`}
                                        title="Pagamenti & credenziali"
                                    >
                                        <Wallet size={14} />
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => startEdit(e)}
                                        data-testid={`employee-edit-${e.id}`}
                                    >
                                        <Pencil size={14} />
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-red-600 hover:text-red-700"
                                        onClick={() => remove(e)}
                                        data-testid={`employee-delete-${e.id}`}
                                    >
                                        <Trash2 size={14} />
                                    </Button>
                                </TableCell>
                            </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="text-xs uppercase tracking-wider text-sf-muted">
                {label}
            </Label>
            <div className="mt-1.5">{children}</div>
        </div>
    );
}
